import { Button, Textarea } from '@heroui/react'
import { useEffect, useState } from 'react'
import type { RadarConfig } from '../../../../types'
import { SavedCheckmark } from '../../pages/Admin/usePersistedConfig'

// Remove top-level await and create a function to fetch logo
const fetchDefaultLogo = () => fetch('/defaultLogo.svg').then(res => res.text())

interface LogoSettingsProps {
  config: RadarConfig | null
  setConfig: (config: RadarConfig) => void
}

export const LogoSettings = ({ config, setConfig }: LogoSettingsProps) => {
  const [defaultLogoSvg, setDefaultLogoSvg] = useState<string>('')
  const [logoPreviewError, setLogoPreviewError] = useState<string | null>(null)
  const [logoModified, setLogoModified] = useState(false)
  const [localLogoSvg, setLocalLogoSvg] = useState('')
  const [svgSaved, setSvgSaved] = useState(false)

  useEffect(() => {
    fetchDefaultLogo().then(setDefaultLogoSvg)
  }, [])

  useEffect(() => {
    if (config) {
      setLocalLogoSvg(config.logoSvg || defaultLogoSvg)
    }
  }, [config, defaultLogoSvg])

  if (!config) return null

  // Function to validate SVG content
  const validateSvgContent = (svgContent: string): boolean => {
    try {
      // Check if it contains <svg and ends with </svg>
      // Allow for XML declaration at the beginning
      if (
        !svgContent.trim().includes('<svg') ||
        !svgContent.trim().endsWith('</svg>')
      ) {
        setLogoPreviewError(
          'Invalid SVG format. Content must include <svg and end with </svg>'
        )
        return false
      }

      // Create a DOM parser to validate the SVG
      const parser = new DOMParser()
      const doc = parser.parseFromString(svgContent, 'image/svg+xml')

      // Check for parsing errors
      const parserError = doc.querySelector('parsererror')
      if (parserError) {
        setLogoPreviewError('Invalid SVG content')
        return false
      }

      setLogoPreviewError(null)
      return true
    } catch (_err) {
      setLogoPreviewError('Error validating SVG content')
      return false
    }
  }

  const handleLogoSvgChange = (svgContent: string) => {
    validateSvgContent(svgContent)
    setLocalLogoSvg(svgContent)
    setLogoModified(true)
  }

  const handleLogoSvgSave = () => {
    if (!logoPreviewError && logoModified && config) {
      setConfig({ ...config, logoSvg: localLogoSvg })
      setLogoModified(false)
      setSvgSaved(true)
      setTimeout(() => setSvgSaved(false), 1200)
    }
  }

  return (
    <div data-testid="logo-settings">
      <h3
        className="text-lg font-medium text-[var(--ds-text)] mb-4"
        data-testid="logo-settings-title"
      >
        Logo
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div
            className="text-xs text-[var(--ds-text-subtle)] mb-2"
            id="svg-instructions"
            data-testid="svg-instructions"
          >
            Paste your SVG code here (must include &lt;svg&gt; tags). SVGs using
            currentColor for fill/stroke will adapt to light/dark themes
            automatically.
          </div>
          <div
            style={{
              position: 'relative',
              display: 'inline-block',
              width: '100%'
            }}
          >
            <Textarea
              value={localLogoSvg}
              onChange={e => handleLogoSvgChange(e.target.value)}
              onBlur={handleLogoSvgSave}
              rows={10}
              data-testid="logo-svg-textarea"
              aria-labelledby="svg-instructions"
              aria-describedby={
                logoPreviewError ? 'logo-error-message' : undefined
              }
              classNames={{
                input:
                  'text-xs font-mono group-data-[has-value=true]:text-[var(--ds-text)] outline-none pr-7',
                inputWrapper:
                  'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] data-[focused=true]:border-[var(--ds-border-focused)]'
              }}
            />
            <SavedCheckmark isSaved={svgSaved} />
          </div>
          {logoPreviewError && (
            <div
              className="mt-2 text-sm text-[var(--ds-text-danger)]"
              id="logo-error-message"
              data-testid="logo-error-message"
              role="alert"
            >
              {logoPreviewError}
            </div>
          )}
          <div
            className="mt-4 flex items-center gap-2"
            data-testid="logo-actions"
          >
            <Button
              variant="bordered"
              className="text-[var(--ds-text)] rounded-[3px] h-8 px-3 border border-[var(--ds-border)] bg-transparent hover:bg-transparent"
              size="sm"
              disableRipple
              onPress={() => {
                setLocalLogoSvg(defaultLogoSvg)
                setLogoModified(true)
                validateSvgContent(defaultLogoSvg)
              }}
              data-testid="reset-logo-button"
              aria-label="Reset logo to default"
            >
              Reset to Default
            </Button>
            <Button
              variant="bordered"
              className="text-[var(--ds-text)] font-bold rounded-[3px] h-8 px-3 border border-[var(--ds-border-focused)] bg-transparent hover:bg-transparent"
              size="sm"
              disableRipple
              isDisabled={!logoModified}
              onPress={handleLogoSvgSave}
              data-testid="save-logo-button"
              aria-label="Save logo changes"
            >
              Save Logo
            </Button>
          </div>
        </div>
        <div>
          <div
            className="text-xs text-[var(--ds-text-subtle)] mb-2"
            id="preview-label"
            data-testid="preview-label"
          >
            Preview
          </div>
          <div
            className="border border-[var(--ds-border)] rounded-[3px] p-4 flex items-center justify-center"
            style={{ minHeight: '200px' }}
            data-testid="logo-preview-container"
            role="img"
            aria-label="Logo preview"
          >
            {!logoPreviewError && localLogoSvg && (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{ color: 'var(--ds-text)' }}
                data-testid="logo-preview"
                role="img"
                aria-label="Current logo preview"
                // Using dangerouslySetInnerHTML is necessary for SVG preview
                // The content is validated by validateSvgContent before rendering
                //biome-ignore lint/security/noDangerouslySetInnerHtml: only a risk for the admin of the app
                dangerouslySetInnerHTML={{ __html: localLogoSvg }}
              />
            )}
            {logoPreviewError && (
              <div
                className="text-sm text-[var(--ds-text-danger)]"
                data-testid="preview-error-message"
                role="alert"
              >
                Invalid SVG preview
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
